import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { get, ref } from 'firebase/database';
import { db } from '../../config/firebase';
import {
  getBranchFromCourses,
  getStudentDisplayName,
  resolveActiveCourseContexts,
  resolvePortfolioCourses,
} from '../../utils/studentIdentity';

const toArray = (value) =>
  Object.entries(value || {}).map(([id, item]) => ({
    id,
    ...(item || {}),
  }));

const InfoTile = ({ label, value }) => (
  <div className="rounded-[22px] border border-stone-200 bg-stone-50 px-4 py-4">
    <p className="text-xs font-medium text-stone-500">{label}</p>
    <p className="mt-1.5 text-base font-semibold text-stone-950">{value || 'Not set'}</p>
  </div>
);

const StudentPortfolioPage = () => {
  const { academyStudentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadPortfolio = async () => {
      try {
        const [studentsSnapshot, usersSnapshot, enrollmentsSnapshot, coursesSnapshot] =
          await Promise.all([
            get(ref(db, 'students')),
            get(ref(db, 'users')),
            get(ref(db, 'enrollments')),
            get(ref(db, 'courses')),
          ]);

        const students = studentsSnapshot.val() || {};
        const users = usersSnapshot.val() || {};
        const courses = coursesSnapshot.val() || {};
        const studentEntry = Object.entries(students).find(
          ([, student]) => student?.academyStudentId === academyStudentId,
        );

        if (!studentEntry) {
          setData({ missing: true });
          setLoading(false);
          return;
        }

        const [studentId, student] = studentEntry;
        const user = users[studentId] || {};
        const enrollments = toArray(enrollmentsSnapshot.val()).filter(
          (item) => item.studentId === studentId,
        );

        setData({
          student,
          user,
          courses,
          enrollments,
        });
      } catch (error) {
        console.error('Failed to load student portfolio:', error);
        setData({ error: true });
      } finally {
        setLoading(false);
      }
    };

    loadPortfolio();
  }, [academyStudentId]);

  const completedCourses = useMemo(
    () =>
      resolvePortfolioCourses({
        enrollments: data?.enrollments || [],
        courses: data?.courses || {},
      }),
    [data],
  );

  const activeCourses = useMemo(
    () =>
      resolveActiveCourseContexts({
        enrollments: data?.enrollments || [],
        courses: data?.courses || {},
      }),
    [data],
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-100 px-4">
        <div className="text-sm text-stone-600">Loading portfolio...</div>
      </div>
    );
  }

  if (!data || data.missing || data.error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-100 px-4 py-10">
        <div className="w-full max-w-lg rounded-[32px] border border-stone-200 bg-white px-6 py-8 text-center shadow-[0_24px_60px_-40px_rgba(28,25,23,0.35)]">
          <h1 className="text-3xl font-semibold tracking-tight text-stone-950">
            Portfolio unavailable
          </h1>
          <p className="mt-3 text-sm leading-6 text-stone-600">
            We could not find a public portfolio for this student yet.
          </p>
          <Link
            to="/academy"
            className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white transition hover:bg-[#b91c66]"
          >
            Visit academy
          </Link>
        </div>
      </div>
    );
  }

  const displayName = getStudentDisplayName(data.user, data.student);
  const publicProfile = data.student?.publicProfile || {};
  const branch = getBranchFromCourses({
    student: data.student,
    enrollments: data.enrollments,
    courses: data.courses,
  });
  const photoUrl =
    data.student?.documents?.photoURL ||
    data.student?.personalDetails?.photoURL ||
    data.user?.photoURL ||
    '/images/profile.png';
  const socialLinks = [
    { label: 'Instagram', value: publicProfile.instagram },
    { label: 'Facebook', value: publicProfile.facebook },
    { label: 'Website', value: publicProfile.website },
  ].filter((item) => item.value);

  return (
    <div className="min-h-screen bg-stone-100 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-3 text-sm font-semibold text-stone-700">
            <img src="/logo.png" alt="Brissbella" className="h-10 w-auto object-contain" />
            Brissbella Academy
          </Link>
          <Link
            to="/academy"
            className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-stone-300 bg-white px-4 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            View academy
          </Link>
        </div>

        <section className="overflow-hidden rounded-[36px] border border-stone-200 bg-white shadow-[0_26px_60px_-40px_rgba(28,25,23,0.35)]">
          <div className="grid gap-6 px-6 py-6 lg:grid-cols-[17rem_minmax(0,1fr)] lg:px-8 lg:py-8">
            <div className="rounded-[30px] bg-[#521739] px-5 py-5 text-white">
              <img
                src={photoUrl}
                alt={displayName}
                className="h-48 w-full rounded-[24px] object-cover"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = '/images/profile.png';
                }}
              />
              <div className="mt-5">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-white/65">
                  Student portfolio
                </p>
                <p className="mt-2 text-2xl font-semibold leading-tight">{displayName}</p>
                <p className="mt-3 text-sm text-white/75">{academyStudentId}</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-500">
                  Public profile
                </p>
                <h1 className="mt-2 text-4xl font-semibold tracking-tight text-stone-950">
                  {displayName}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-stone-600">
                  {publicProfile.bio ||
                    'Completed learning highlights and academy achievements are shown here once a student finishes active study.'}
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <InfoTile label="Branch" value={branch} />
                <InfoTile label="Completed courses" value={completedCourses.length} />
                <InfoTile
                  label="Status"
                  value={activeCourses.length ? 'Active learner' : 'Graduate profile'}
                />
              </div>

              {socialLinks.length ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  {socialLinks.map((item) => (
                    <a
                      key={item.label}
                      href={item.value}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-[22px] border border-stone-200 bg-stone-50 px-4 py-4 text-sm font-semibold text-stone-700 transition hover:bg-white"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {activeCourses.length ? (
          <section className="rounded-[32px] border border-stone-200 bg-white px-6 py-6 shadow-[0_22px_50px_-38px_rgba(28,25,23,0.32)]">
            <h2 className="text-2xl font-semibold tracking-tight text-stone-950">
              Portfolio updates after active study
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-stone-600">
              This student is still actively studying. The public portfolio stays minimal until active courses are completed.
            </p>
          </section>
        ) : null}

        <section className="rounded-[32px] border border-stone-200 bg-white px-6 py-6 shadow-[0_22px_50px_-38px_rgba(28,25,23,0.32)]">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              Achievements
            </p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-stone-950">
              Completed courses
            </h2>
          </div>

          {!completedCourses.length ? (
            <div className="mt-5 rounded-[24px] border border-dashed border-stone-300 bg-stone-50 px-5 py-10 text-center">
              <p className="text-lg font-semibold text-stone-900">No completed courses yet</p>
              <p className="mt-2 text-sm text-stone-600">
                Completed academy courses will appear here once they are finished successfully.
              </p>
            </div>
          ) : (
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {completedCourses.map((course) => (
                <article
                  key={course.id}
                  className="rounded-[26px] border border-stone-200 bg-stone-50 px-5 py-5"
                >
                  <p className="text-lg font-semibold text-stone-950">
                    {course.courseName}
                  </p>
                  <div className="mt-4 grid gap-3">
                    <InfoTile
                      label="Enrollment date"
                      value={
                        course.enrollmentDate
                          ? new Date(course.enrollmentDate).toLocaleDateString()
                          : 'Not set'
                      }
                    />
                    <InfoTile
                      label="Completion"
                      value={`${Math.min(Number(course.progress || 100), 100)}%`}
                    />
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default StudentPortfolioPage;
